class FilterSort extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      container: 'filter-sort',
      form: '[js-filter-form]',
      filter: '[js-filter]',
    };

    this._events = {
      change: 'filter:change',
    };

    this._cache = [];

    this._debouncedOnSubmit = this._debounce((event) => {
      this._onSubmitHandler(event);
    }, 800);
  }

  connectedCallback() {
    this._form = this.querySelector(this._selectors.form);
    this._loader = this.querySelector('.filter-loader');
    this.querySelector('.loading-spinner').classList.remove('hidden');
    this._sortButton = this.querySelector('[js-sort-open]');
    this._sortContainer = this.querySelector('[js-sort-container]');
    this._sortDropdown = this._sortContainer?.querySelector('div');
    this._setListeners();
  }

  _setListeners() {
    window.addEventListener('popstate', this._onHistoryChange);
    this._form.addEventListener('input', this._debouncedOnSubmit);

    // Sort menu toggle
    if (this._sortButton && this._sortContainer && this._sortDropdown) {
      this._sortButton.addEventListener('click', this._toggleSortMenu.bind(this));
      this._sortButton.addEventListener('keydown', this._handleButtonKeydown.bind(this));
      document.addEventListener('click', this._handleOutsideClick.bind(this));
      this._sortDropdown.addEventListener('keydown', this._handleDropdownKeydown.bind(this));

      // Add keyboard support for menu items
      this._sortDropdown.querySelectorAll('label').forEach((item) => {
        item.addEventListener('keydown', this._handleMenuItemKeydown.bind(this));
      });
    }
  }

  // Updates the page with the new search results
  _renderComponent(parsedHTML) {
    const containers = parsedHTML.querySelectorAll(this._selectors.container);
    var selector = '';
    containers.forEach((container) => {
      if (container.dataset.tab == 'filter') {
        selector = '[js-filter-container]';
      } else {
        selector = '[js-sort-container]';
      }
    });
    const scopedHTML = parsedHTML.querySelector(selector);
    this._refreshElements([this._selectors.filter], scopedHTML, this);

    // Update sort button label
    const sortLabel = parsedHTML.querySelector('[js-sort-label]');
    if (sortLabel) {
      const currentSortLabel = this.querySelector('[js-sort-label]');
      if (currentSortLabel) {
        currentSortLabel.textContent = sortLabel.textContent;
      }
    }

    document.querySelectorAll('s-collection-grid').forEach((grid) => {
      document.body.addEventListener('click', grid._drawerClose, true);
    });
  }

  _onSubmitHandler = (evt) => {
    evt.preventDefault();

    const formData = new FormData(this._form);
    const searchParams = new URLSearchParams(formData);

    // We only want products that are not sold out so add this option if it is missing
    if (!searchParams.has('filter.v.availability')) {
      searchParams.append('filter.v.availability', 1);
    }

    // TODO: unset filter.v.availability if it is the only search param, maybe

    let newParams = searchParams.toString();
    this._propagateChange(newParams);
    this._closeSortMenu();
  };

  _onHistoryChange = (evt) => {
    const searchParams = evt.state ? evt.state.searchParams : FilterSort.searchParamsInitial;
    if (searchParams === FilterSort.searchParamsPrev) return;
    this._propagateChange(searchParams, false);
    // change didn't come from submit, so reset field
    this._onSubmitHandlerEvent = null;
  };

  async _propagateChange(searchParams, updateURLHash = true) {
    this._loader.classList.remove('hidden');

    const url = `${window.location.pathname}?${searchParams}`;
    const filterDataUrl = (element) => element.url === url;
    const parsedHTML = this._cache.some(filterDataUrl)
      ? this._markupFromCache(filterDataUrl)
      : await this._markupFromFetch(url);
    document.dispatchEvent(
      new CustomEvent('filter:change', {
        detail: { parsedHTML },
      }),
    );
    this._renderComponent(parsedHTML); // re-render myself
    document.querySelectorAll('s-collection-grid').forEach((grid) => {
      document.body.addEventListener('click', grid._drawerClose, true);
    });
    if (updateURLHash) {
      history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }
    this._loader.classList.add('hidden');
  }

  _markupFromCache(url) {
    const html = this._cache.find(url).html;
    return new DOMParser().parseFromString(html, 'text/html');
  }

  _markupFromFetch(url) {
    let cache = this._cache;
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        cache = [...cache, { html, url }];
        return parsedHTML;
      });
  }

  _debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  _toggleSortMenu(event) {
    event.stopPropagation();
    const isHidden = this._sortDropdown.classList.contains('hidden');

    if (isHidden) {
      this._sortDropdown.classList.remove('hidden');
      this._sortButton.setAttribute('aria-expanded', 'true');
    } else {
      this._closeSortMenu();
    }
  }

  _openSortMenu() {
    this._sortDropdown.classList.remove('hidden');
    this._sortButton.setAttribute('aria-expanded', 'true');

    // Focus first menu item
    const firstMenuItem = this._sortDropdown.querySelector('label');
    if (firstMenuItem) {
      firstMenuItem.focus();
    }
  }

  _closeSortMenu() {
    this._sortDropdown.classList.add('hidden');
    this._sortButton.setAttribute('aria-expanded', 'false');
    this._sortButton.focus();
  }

  _handleButtonKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._toggleSortMenu(event);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this._openSortMenu();
    }
  }

  _handleOutsideClick(event) {
    if (!this._sortContainer.contains(event.target) && !this._sortButton.contains(event.target)) {
      // Not using _closeSortMenu because it focuses it changes focus to the toggle button
      this._sortDropdown.classList.add('hidden');
      this._sortButton.setAttribute('aria-expanded', 'false');
    }
  }

  _handleDropdownKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this._closeSortMenu();
    }
  }

  _handleMenuItemKeydown(event) {
    const menuItems = Array.from(this._sortDropdown.querySelectorAll('label'));
    const currentIndex = menuItems.indexOf(event.target);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % menuItems.length;
        menuItems[nextIndex].focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? menuItems.length - 1 : currentIndex - 1;
        menuItems[prevIndex].focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        const inputId = event.target.getAttribute('for');
        const input = document.getElementById(inputId);
        if (input) {
          input.checked = true;
          // Trigger immediate form submission for sort changes
          this._onSubmitHandler({ preventDefault: () => {}, target: input });
          this._closeSortMenu();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this._closeSortMenu();
        break;
    }
  }

  // Updates the page with the new search results and filter states
  _refreshElements(selectors, newHTML, container) {
    selectors.forEach((selector) => {
      const newEls = newHTML.querySelectorAll(selector);
      const oldEls = container.querySelectorAll(selector);
      oldEls.forEach((oldEl, idx) => {
        if (newEls[idx]) {
          oldEl.innerHTML = newEls[idx].innerHTML;

          oldEl.querySelectorAll('[tabindex="-1"]').forEach((item) => {
            item.setAttribute('tabindex', '0');
          });
          // Event delegation is now handled in _setListeners, no need to add individual listeners
        } else {
          oldEl.remove();
        }
      });
    });
  }
}
// Class variables
FilterSort.searchParamsInitial = window.location.search.slice(1);
FilterSort.searchParamsPrev = window.location.search.slice(1);
customElements.define('filter-sort-container', FilterSort);

class CollectionGrid extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      productGrid: '[js-products-grid]',
      filterDrawer: '[js-filter-drawer]',
      filterOpen: '[js-filter-open]',
      filterClose: '[js-filter-close]',
      detailsButton: '[open-details-drawer]',
    };

    this._refresh = ['[js-products-grid]', '[js-product-count]'];

    // cache fetches for performance
    this._cache = [];
  }

  connectedCallback() {
    this.filterDrawer = this.querySelector(this._selectors.filterDrawer);
    this.filterOpen = this.querySelectorAll(this._selectors.filterOpen);
    this.filterClose = this.querySelectorAll(this._selectors.filterClose);
    this.detailButtons = this.querySelectorAll(this._selectors.detailsButton);
    this.detailButtons.forEach((button) => {
      button.addEventListener('click', this._detailsDrawer);
    });
    this._setListeners();
  }

  _setListeners() {
    document.addEventListener('filter:change', this._handleFilterChange);
    this.filterOpen.forEach((button) => {
      button.addEventListener('click', this._openFilter);
    });

    // Use event delegation for keyboard support on filter labels
    this.filterDrawer.addEventListener('keydown', this._handleFilterItemKeydown.bind(this));
  }

  _openFilter = () => {
    this.filterClose.forEach((close, index) => {
      close.addEventListener('click', this._closeFilter);
      if (index === 0) close.focus();
    });
    this.filterDrawer.style.transform = 'translateX(0)';
    this.filterDrawer.querySelectorAll('[tabindex="-1"]').forEach((item) => {
      item.setAttribute('tabindex', '0');
    });
    var main = document.querySelector('#main');
    var button = document.querySelector('[aria-label="Open Form"]');
    if (button) button.style.zIndex = '-1';
    main.classList.add('js-drawer-open');
    document.body.classList.add('js-drawer-open');
    document.body.classList.add('!overflow-hidden');
    setTimeout(() => {
      document.body.addEventListener('click', this._drawerClose, true);
    }, 500);
  };

  _closeFilter = (evt) => {
    if (evt) evt.preventDefault();
    var main = document.querySelector('#main');
    this.filterDrawer.style.transform = 'translateX(200%)';
    this.filterDrawer.querySelectorAll('[tabindex="0"]').forEach((item) => {
      item.setAttribute('tabindex', '-1');
    });
    var button = document.querySelector('[aria-label="Open Form"]');
    if (button) button.style.zIndex = '99999';
    main.classList.remove('js-drawer-open');
    document.body.classList.remove('!overflow-hidden');
    document.body.removeEventListener('click', this._drawerClose, true);
    setTimeout(() => {
      document.body.classList.remove('js-drawer-open');
    }, 500);
  };

  _drawerClose = (evt) => {
    var main = document.querySelector('#main');
    if (!this.filterDrawer.contains(evt.target) && main.classList.contains('js-drawer-open')) {
      this._closeFilter();
    }
    document.body.removeEventListener('click', this._drawerClose, true);
  };

  _handleFilterChange = (evt) => {
    const { parsedHTML } = evt.detail;
    const scopedHTML = parsedHTML.querySelector(`[data-id="${this.dataset.id}"]`);
    this._refreshElements(this._refresh, scopedHTML, this);
    console.log('FILTER CHANGE');
    // Restore focus after content refresh
    this._restoreFocus();
  };

  _refreshElements(selectors, newHTML, container) {
    selectors.forEach((selector) => {
      const newEls = newHTML.querySelectorAll(selector);
      const oldEls = container.querySelectorAll(selector);
      oldEls.forEach((oldEl, idx) => {
        if (newEls[idx]) {
          oldEl.innerHTML = newEls[idx].innerHTML;
        } else {
          oldEl.remove();
        }
      });
    });
  }

  _handleFilterItemKeydown(event) {
    if (event.target.tagName === 'A' || event.target.tagName === 'BUTTON') {
      return;
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        const inputId = event.target.getAttribute('for');
        const input = inputId ? document.getElementById(inputId) : event.target;
        if (input) {
          // Store focus information before triggering change
          this._storeFocusState(event.target);

          input.checked = input.checked ? false : true;

          // Trigger the form submission to update filters
          const form = input.closest('[js-filter-form]');
          if (form) {
            const changeEvent = new Event('input', { bubbles: true });
            form.dispatchEvent(changeEvent);
          }
        }
        break;
      case 'Escape':
        event.preventDefault();
        this._closeFilter();
        break;
    }
  }

  _storeFocusState(element) {
    // Store information to restore focus after content refresh
    this._focusRestoreData = {
      elementId: element.id,
      elementFor: element.getAttribute('for'),
      elementText: element.textContent?.trim(),
      elementTagName: element.tagName,
    };
  }

  _restoreFocus() {
    if (!this._focusRestoreData) return;

    const { elementId, elementFor, elementText, elementTagName } = this._focusRestoreData;
    let targetElement = null;

    // Try to find the element by ID first
    if (elementId) {
      targetElement = document.getElementById(elementId);
    }

    // If not found and it's a label, try to find by 'for' attribute
    if (!targetElement && elementFor && elementTagName === 'LABEL') {
      targetElement = document.querySelector(`label[for="${elementFor}"]`);
    }

    // If still not found, try to find by text content and tag name
    if (!targetElement && elementText) {
      const elements = this.filterDrawer.querySelectorAll(elementTagName.toLowerCase());
      targetElement = Array.from(elements).find((el) => el.textContent?.trim() === elementText);
    }

    console.log('TARGET ::', targetElement.focus());

    if (targetElement) {
      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        targetElement.focus();
      }, 500);
    }

    // Clear the stored focus data
    this._focusRestoreData = null;
  }
}

customElements.define('s-collection-grid', CollectionGrid);

class ActiveFilters extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      remove: '[js-remove]',
    };

    this._refresh = ['[js-active-filter]'];
  }

  connectedCallback() {
    document.addEventListener('filter:change', this._handleFilterChange);
    this._setListeners();
  }

  _setListeners() {
    this.querySelectorAll(this._selectors.remove).forEach((remove) =>
      remove.addEventListener('click', this._handleRemove),
    );
  }

  _handleRemove = (evt) => {
    evt.preventDefault();
    const { name, value } = evt.currentTarget.dataset;
    const input = document.querySelector(`filter-sort [name="${name}"][value="${value}"]`);
    if (input) input.click();
  };

  _handleFilterChange = (evt) => {
    const { parsedHTML } = evt.detail;
    const scopedHTML = parsedHTML.querySelector('active-filters');
    this.innerHTML = scopedHTML.innerHTML;
    this._setListeners();
  };
}
customElements.define('active-filters', ActiveFilters);
